import { useEffect, useRef } from "react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { get } from "../../../common/api/apiShared";
import { getSettings } from "../../../common/settingsProvider.ts";
import { useQuery } from "@tanstack/react-query";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";

interface HubSpotTokenResponse {
    token: string;
}

export function useHubSpotChat() {
    const { emailAddress } = useUserContext();
    const { addonsFeatures } = usePlanContext();

    const listenerRegistered = useRef(false);
    const widgetClosedListenerRegistered = useRef(false);
    const settings = getSettings();

    function setWidgetVisibility(visible: boolean) {
        const container = document.getElementById("hubspot-messages-iframe-container");
        if (container) {
            container.style.setProperty("display", visible ? "initial" : "none", "important");
        }
    }

    const { isLoading, data } = useQuery({
        queryKey: ["hubspot-token"],
        queryFn: () => get<HubSpotTokenResponse>("/hubspot/identification-token"),
        enabled: !!emailAddress && settings.isRunningOnProduction && !addonsFeatures.research,
        staleTime: 8 * 60 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (data) {
            if (listenerRegistered.current || !data?.token || !emailAddress) {
                return;
            }
            listenerRegistered.current = true;

            window.hsConversationsSettings = {
                ...window.hsConversationsSettings,
                identificationEmail: emailAddress,
                identificationToken: data.token,
            };
        }
        // eslint-disable-next-line
    }, [data]);

    const openChat = () => {
        const loadWidget = () => {
            const status = window.HubSpotConversations!.widget.status();
            if (!status.loaded) {
                window.HubSpotConversations!.widget.load();
                window.HubSpotConversations!.on("widgetLoaded", () => {
                    setTimeout(() => window.HubSpotConversations!.widget.open(), 200);
                });
            } else {
                setTimeout(() => setWidgetVisibility(true), 200);
                window.HubSpotConversations!.widget.open();
            }
            if (!widgetClosedListenerRegistered.current) {
                widgetClosedListenerRegistered.current = true;
                window.HubSpotConversations!.on("widgetClosed", () => setWidgetVisibility(false));
            }
        };

        if (window.HubSpotConversations) {
            loadWidget();
        } else {
            if (!window.hsConversationsOnReady) {
                window.hsConversationsOnReady = [];
            }
            window.hsConversationsOnReady.push(loadWidget);
        }
    };

    return { openChat, isLoading, isAvailable: settings.isRunningOnProduction };
}
